import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { useCsatSettings, useUpdateCsatSettings } from '../hooks/useSettings';
import {
  CSAT_PROMPT_DELAY_MAX,
  CSAT_PROMPT_DELAY_MIN,
  CSAT_PROMPT_TEXT_MAX_LENGTH,
  CsatSettings,
} from '../services/settingsService';

interface FormState {
  enabled: boolean;
  promptDelayMinutes: number;
  promptText: string;
  thankyouText: string;
}

function fromSettings(s: CsatSettings | undefined): FormState {
  return {
    enabled: s?.enabled ?? false,
    promptDelayMinutes: s?.promptDelayMinutes ?? 2,
    promptText: s?.promptText ?? '',
    thankyouText: s?.thankyouText ?? '',
  };
}

export default function FeedbackSettings() {
  const { data: settings, isLoading } = useCsatSettings();
  const update = useUpdateCsatSettings();

  const [draft, setDraft] = useState<FormState>(fromSettings(undefined));
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!hydrated && settings) {
      setDraft(fromSettings(settings));
      setHydrated(true);
    }
  }, [hydrated, settings]);

  if (isLoading || !hydrated) {
    return <div className="py-8 text-center text-ink-3">Loading...</div>;
  }

  const saved = fromSettings(settings);
  const dirty =
    draft.enabled !== saved.enabled ||
    draft.promptDelayMinutes !== saved.promptDelayMinutes ||
    draft.promptText !== saved.promptText ||
    draft.thankyouText !== saved.thankyouText;

  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!draft.promptText.trim()) {
    errors.promptText = 'Prompt text is required.';
  } else if (draft.promptText.length > CSAT_PROMPT_TEXT_MAX_LENGTH) {
    errors.promptText = `Keep it under ${CSAT_PROMPT_TEXT_MAX_LENGTH} characters.`;
  }
  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = async () => {
    if (hasErrors) return;
    try {
      await update.mutateAsync({
        enabled: draft.enabled,
        prompt_delay_minutes: draft.promptDelayMinutes,
        prompt_text: draft.promptText.trim(),
        thankyou_text: draft.thankyouText.trim() || null,
      });
      toast.success('Feedback settings saved');
    } catch {
      // hook already toasted
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-panel border border-line bg-surface p-6 shadow-s">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink">Customer Feedback (CSAT)</h3>
            <p className="mt-1 text-sm text-ink-2">
              After a conversation is resolved, send a 1–5 star rating prompt to the
              customer over WhatsApp. The score lands in the conversation timeline
              and the analytics dashboard. Prompts only send inside the 24h
              customer-service window.
            </p>
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              className="peer sr-only"
            />
            <div className="relative h-6 w-11 rounded-full bg-line transition-colors peer-checked:bg-brand">
              <div className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-s transition-transform peer-checked:translate-x-5" />
            </div>
            <span className="text-sm font-medium text-ink">
              {draft.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Form fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-2">
                Delay after resolution: {draft.promptDelayMinutes} minute
                {draft.promptDelayMinutes === 1 ? '' : 's'}
              </label>
              <input
                type="range"
                min={CSAT_PROMPT_DELAY_MIN}
                max={CSAT_PROMPT_DELAY_MAX}
                value={draft.promptDelayMinutes}
                onChange={(e) => setDraft({ ...draft, promptDelayMinutes: Number(e.target.value) })}
                disabled={!draft.enabled}
                className="mt-2 w-full accent-brand disabled:opacity-40"
              />
              <p className="mt-1 text-xs text-ink-3">
                How long after the conversation is resolved before the prompt fires.
                Shorter → higher response rate; longer → the customer has more time to
                form an opinion.
              </p>
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label className="block text-sm font-medium text-ink-2">Prompt text</label>
                <span
                  className={
                    draft.promptText.length > CSAT_PROMPT_TEXT_MAX_LENGTH
                      ? 'text-xs text-danger'
                      : 'text-xs text-ink-3'
                  }
                >
                  {draft.promptText.length} / {CSAT_PROMPT_TEXT_MAX_LENGTH}
                </span>
              </div>
              <textarea
                value={draft.promptText}
                onChange={(e) => setDraft({ ...draft, promptText: e.target.value })}
                rows={3}
                disabled={!draft.enabled}
                className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:opacity-40"
              />
              {errors.promptText && <p className="mt-1 text-xs text-danger">{errors.promptText}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-2">
                Thank-you message (optional)
              </label>
              <input
                type="text"
                value={draft.thankyouText}
                onChange={(e) => setDraft({ ...draft, thankyouText: e.target.value })}
                placeholder="Thanks for your feedback!"
                disabled={!draft.enabled}
                className="mt-1 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:opacity-40"
              />
              <p className="mt-1 text-xs text-ink-3">
                Sent automatically after the customer taps a rating. Leave blank to
                skip the follow-up.
              </p>
            </div>
          </div>

          {/* Live WhatsApp-style preview */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">
              Preview
            </p>
            <div className="rounded-lg border border-white/10 bg-[#0b141a] p-3">
              <WhatsAppBubble
                body={draft.promptText.trim() || 'Your prompt text will show here.'}
              />
            </div>
            <p className="mt-2 text-xs text-ink-3">
              How the prompt will appear in WhatsApp. The 5 star rows + "Rate now"
              button are sent as a tappable interactive list.
            </p>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={!dirty || hasErrors || update.isPending}
            className="rounded-pill bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {update.isPending ? 'Saving...' : 'Save'}
          </button>
          <span className="text-xs text-ink-3">{dirty ? 'Unsaved changes' : 'Saved'}</span>
        </div>
      </div>
    </div>
  );
}

function WhatsAppBubble({ body }: { body: string }) {
  return (
    <div className="max-w-xs rounded-lg bg-[#202c33] p-2 text-sm text-gray-100 shadow">
      <div className="border-b border-white/10 pb-1.5 text-xs font-semibold text-gray-400">
        Quick feedback
      </div>
      <p className="mt-1.5 whitespace-pre-wrap break-words">{body}</p>
      <div className="mt-2 space-y-1.5">
        {[5, 4, 3, 2, 1].map((score) => (
          <div
            key={score}
            className="flex items-center justify-between rounded border border-white/10 bg-white/[0.03] px-2 py-1.5"
          >
            <div>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) =>
                  n <= score ? (
                    <StarSolid key={n} className="h-3 w-3 text-amber-400" />
                  ) : (
                    <StarIcon key={n} className="h-3 w-3 text-gray-600" />
                  )
                )}
              </div>
              <div className="text-[10px] text-gray-400">
                {SCORE_LABELS[score]}
              </div>
            </div>
            <div className="text-[10px] text-gray-500">{SCORE_DESCRIPTIONS[score]}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 border-t border-white/10 pt-1.5 text-center text-xs font-medium text-emerald-400">
        Rate now
      </div>
      <p className="mt-1 text-[10px] text-gray-500">Tap a star to rate</p>
    </div>
  );
}

const SCORE_LABELS: Record<number, string> = {
  5: 'Excellent',
  4: 'Good',
  3: 'Okay',
  2: 'Poor',
  1: 'Terrible',
};

const SCORE_DESCRIPTIONS: Record<number, string> = {
  5: 'Loved it',
  4: 'Mostly good',
  3: 'Could be better',
  2: 'Not great',
  1: 'Did not help',
};
